import {
  lazy,
  Suspense,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type SyntheticEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon, Loading, Modal } from '@/shared/ui';
import { HELP_BRIEFS } from '../content/briefs.generated';

// The articles need the whole help content (about 700 KB); it loads on the first click only.
const HelpArticles = lazy(async () => ({
  default: (await import('./HelpArticles')).HelpArticles,
}));

/** Space between the bulb and its preview, and between the preview and the window edge. */
const GAP = 6;
const EDGE = 8;
/** Moving the pointer across a bulb does not flash the preview. */
const SHOW_DELAY = 120;
/** Lets the pointer cross the gap into the preview before it closes. */
const HIDE_DELAY = 150;

/** The bulb may sit in a clickable tile or row; opening help must not also open that. */
const stop = (event: SyntheticEvent) => {
  event.stopPropagation();
};

export interface HelpBulbProps {
  /**
   * Help URLs, the most specific first: the screen section that describes the chart
   * (`/help/screens/overview#mrr-trend`), then the topics of the KPIs it shows (`/help/kpis/mrr`).
   */
  topics: readonly string[];
  /** What the bulb explains, for its accessible name ("Help: MRR") and the popup title. */
  label?: string;
  /** Or the ID of the element that names it, such as a card title. */
  labelledBy?: string;
}

/**
 * `button.help-bulb`: a small light bulb next to a KPI, chart or page title. Hovering or focusing
 * it previews its help topics; clicking it opens them in full in a popup.
 */
export function HelpBulb({ topics, label, labelledBy }: HelpBulbProps) {
  const id = useId();
  const anchor = useRef<HTMLButtonElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [preview, setPreview] = useState(false);
  const [reading, setReading] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const known = topics.filter((url) => url in HELP_BRIEFS);

  const show = (delay: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPreview(true);
    }, delay);
  };
  const hide = (delay: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPreview(false);
    }, delay);
  };
  const open = (url: string) => {
    clearTimeout(timer.current);
    setPreview(false);
    const named = labelledBy ? document.getElementById(labelledBy)?.textContent : undefined;
    setTitle(label ?? named?.trim() ?? HELP_BRIEFS[url]?.title ?? 'Help');
    setReading(url);
  };

  // Below the bulb, or above it near the bottom of the window; never past either side.
  useLayoutEffect(() => {
    if (!preview || !anchor.current || !tip.current) return;
    const a = anchor.current.getBoundingClientRect();
    const t = tip.current.getBoundingClientRect();
    const below = a.bottom + GAP;
    const top = below + t.height > window.innerHeight - EDGE ? a.top - GAP - t.height : below;
    const left = Math.max(EDGE, Math.min(a.left - 12, window.innerWidth - EDGE - t.width));
    tip.current.style.top = `${String(Math.max(EDGE, top))}px`;
    tip.current.style.left = `${String(left)}px`;
  }, [preview]);

  // The preview is fixed to the window, so it closes when anything scrolls under it.
  useEffect(() => {
    if (!preview) return;
    const close = () => {
      setPreview(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [preview]);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  const [first, ...more] = known;
  if (first === undefined) return null;
  const lead = HELP_BRIEFS[first];

  return (
    <span className="help-bulb-wrap" onClick={stop} onKeyDown={stop}>
      <button
        ref={anchor}
        type="button"
        className="help-bulb"
        aria-label={labelledBy ? undefined : `Help: ${label ?? lead?.title ?? ''}`}
        aria-labelledby={labelledBy ? `${id}-help ${labelledBy}` : undefined}
        aria-haspopup="dialog"
        aria-describedby={preview ? `${id}-tip` : undefined}
        onMouseEnter={() => {
          show(SHOW_DELAY);
        }}
        onMouseLeave={() => {
          hide(HIDE_DELAY);
        }}
        onFocus={() => {
          show(0);
        }}
        onBlur={() => {
          hide(0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && preview) {
            event.preventDefault();
            hide(0);
          }
        }}
        onClick={() => {
          open(first);
        }}
      >
        {labelledBy && (
          <span className="visually-hidden" id={`${id}-help`}>
            Help:
          </span>
        )}
        <Icon name="bulb" size="sm" />
      </button>
      {preview &&
        createPortal(
          <div
            ref={tip}
            id={`${id}-tip`}
            className="help-pop"
            role="tooltip"
            onMouseEnter={() => {
              show(0);
            }}
            onMouseLeave={() => {
              hide(HIDE_DELAY);
            }}
          >
            {lead && (
              <button
                type="button"
                className="help-pop__lead"
                tabIndex={-1}
                onClick={() => {
                  open(first);
                }}
              >
                {lead.page && <span className="help-pop__page">{lead.page}</span>}
                <span className="help-pop__title">{lead.title}</span>
                <span className="help-pop__text">{lead.text}</span>
              </button>
            )}
            {more.length > 0 && (
              <ul className="help-pop__more" aria-label="More help">
                {more.map((url) => (
                  <li key={url}>
                    <button
                      type="button"
                      className="help-pop__item"
                      tabIndex={-1}
                      onClick={() => {
                        open(url);
                      }}
                    >
                      <span className="help-pop__title">{HELP_BRIEFS[url]?.title}</span>
                      <span className="help-pop__text">{HELP_BRIEFS[url]?.text}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="help-pop__hint">
              Click the bulb to read {known.length > 1 ? 'the articles' : 'the article'}
            </p>
          </div>,
          document.body,
        )}
      <Modal
        open={reading !== null}
        onClose={() => {
          setReading(null);
        }}
        labelledBy={`${id}-title`}
        wide
      >
        <Suspense fallback={<Loading label="Loading help" />}>
          {reading !== null && (
            <HelpArticles
              topics={known}
              initial={reading}
              title={title}
              titleId={`${id}-title`}
              onClose={() => {
                setReading(null);
              }}
            />
          )}
        </Suspense>
      </Modal>
    </span>
  );
}
